import { Cross2Icon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  Heading,
  IconButton,
  Select,
  Switch,
  Text,
} from "@radix-ui/themes";
import { PredictionLineBarChart } from "./prediction-line-bar-chart";
import { useEffect, useMemo, useState } from "react";
import { DashboardFilters } from "../lib/types";
import { useDashboardStatsQuery, usePipelineVersionsQuery } from "@/shared/api";
import { convertFiltersToQueryParams } from "../lib/filters-to-query-params";
import { mergeStatsForComparison } from "../lib/helpers";
import { ComparisonTooltip } from "./comparison-tooltip";
import { useLocalStorage } from "react-use";

export function ComparisonDialog({ filters }: { filters: DashboardFilters }) {
  const { data } = usePipelineVersionsQuery();

  const [versionA, setVersionA] = useLocalStorage(
    "comparison_filter_search1",
    ""
  );
  const [versionAFilter, setVersionAFilter] = useState<string>();
  const [versionB, setVersionB] = useLocalStorage(
    "comparison_filter_search2",
    ""
  );
  const [versionBFilter, setVersionBFilter] = useState<string>();
  const [isBarChart, setIsBarChart] = useLocalStorage(
    "is_comparison_dialog_bar_chart",
    false
  );
  const [isOpen, setIsOpen] = useLocalStorage(
    "is_comparison_dialog_open",
    false
  );
  const [showOnlyMatchedOrders, setShowOnlyMatchedOrders] = useLocalStorage(
    "is_comparison_dialog_show_only_matched_orders",
    false
  );

  const baseStats = useDashboardStatsQuery({
    ...convertFiltersToQueryParams(filters),
    pipeline_version: versionAFilter || undefined,
  });

  const compareStats = useDashboardStatsQuery({
    ...convertFiltersToQueryParams(filters),
    pipeline_version: versionBFilter || undefined,
  });

  const colorA = "rgba(210, 0, 5, 0.44)";
  const colorB = "rgba(0, 143, 74, 0.81)";

  // Memoized merged data for zone-based comparison
  const zoneBasedComparisonData = useMemo(() => {
    return mergeStatsForComparison(
      baseStats.data,
      compareStats.data,
      "zone_based"
    );
  }, [baseStats.data, compareStats.data]);

  const zoneBasedComparisonDataFiltered = useMemo(() => {
    if (!versionAFilter || !versionBFilter) {
      return {
        results: [],
        count: 0,
      };
    }

    const filtered = zoneBasedComparisonData.results.filter((item) =>
      showOnlyMatchedOrders ? item.isMatched : true
    );
    return {
      results: filtered,
      count: filtered.length,
    };
  }, [
    zoneBasedComparisonData,
    showOnlyMatchedOrders,
    versionAFilter,
    versionBFilter,
  ]);

  // Memoized merged data for pin-based comparison
  const pinBasedComparisonData = useMemo(() => {
    return mergeStatsForComparison(
      baseStats.data,
      compareStats.data,
      "pin_based"
    );
  }, [baseStats.data, compareStats.data]);

  const pinBasedComparisonDataFiltered = useMemo(() => {
    if (!versionAFilter || !versionBFilter) {
      return {
        results: [],
        count: 0,
      };
    }

    const filtered = pinBasedComparisonData.results.filter((item) =>
      showOnlyMatchedOrders ? item.isMatched : true
    );
    return {
      results: filtered,
      count: filtered.length,
    };
  }, [
    pinBasedComparisonData,
    showOnlyMatchedOrders,
    versionAFilter,
    versionBFilter,
  ]);

  const zoneBasedLabelMap = {
    [versionAFilter || "Version A"]: "zone_based_f1_score_A",
    [versionBFilter || "Version B"]: "zone_based_f1_score_B",
  };

  const pinBasedLabelMap = {
    [versionAFilter || "Version A"]: "pin_based_f1_score_A",
    [versionBFilter || "Version B"]: "pin_based_f1_score_B",
  };

  useEffect(() => {
    if (versionA) {
      setVersionAFilter(versionA);
    }

    if (versionB) {
      setVersionBFilter(versionB);
    }
  }, []);

  const isZoneBasedChartClickable = (event: any) => {
    const payload = event.activePayload?.[0].payload;

    if (!payload) {
      return false;
    }

    const zoneBasedF1ScoreA = payload.zone_based_f1_score_A;
    const zoneBasedF1ScoreB = payload.zone_based_f1_score_B;

    if (zoneBasedF1ScoreA === null && zoneBasedF1ScoreB === null) {
      return false;
    }

    return true;
  };

  const isPinBasedChartClickable = (event: any) => {
    const payload = event.activePayload?.[0].payload;

    if (!payload) {
      return false;
    }

    const pinBasedF1ScoreA = payload.pin_based_f1_score_A;
    const pinBasedF1ScoreB = payload.pin_based_f1_score_B;

    if (pinBasedF1ScoreA === null && pinBasedF1ScoreB === null) {
      return false;
    }

    return true;
  };

  const versionAOptions = data?.results.filter(
    (version) => version !== versionB
  );
  const versionBOptions = data?.results.filter(
    (version) => version !== versionA
  );

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger>
        <Button size="1" variant="soft" color="gray">
          Compare Pipeline Versions
        </Button>
      </Dialog.Trigger>
      <Dialog.Content minWidth="95vw" minHeight="90dvh">
        <Flex align="center" gap="2" mb="4">
          <Dialog.Title mb="0">F1 Score Chart Comparison</Dialog.Title>
          <Flex gap="2">
            <Switch
              checked={isBarChart}
              onCheckedChange={setIsBarChart}
              size="1"
            />{" "}
            <Text
              size="1"
              weight="medium"
              className={!isBarChart ? "opacity-50" : ""}
            >
              Bar Chart
            </Text>
          </Flex>
          <div className="grow"></div>
          <Text as="label" className="select-none">
            <Flex gap="2" align="center" mr="4">
              <Checkbox
                checked={showOnlyMatchedOrders}
                onCheckedChange={(checked) =>
                  setShowOnlyMatchedOrders(
                    checked === "indeterminate" ? false : checked
                  )
                }
                size="1"
              />{" "}
              <Text size="1" weight="medium">
                Show only matched orders
              </Text>
            </Flex>
          </Text>
          <Select.Root size="1" value={versionA} onValueChange={setVersionA}>
            <Select.Trigger
              placeholder="Initial"
              className="bg-[rgba(243,0,13,0.08)]"
            />
            <Select.Content>
              {versionAOptions?.map((version) => (
                <Select.Item value={version}>{version}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          <Text size="1">vs</Text>
          <Select.Root size="1" value={versionB} onValueChange={setVersionB}>
            <Select.Trigger
              placeholder="v0.1"
              className="bg-[rgba(0,164,51,0.1)]"
            />
            <Select.Content>
              {versionBOptions?.map((version) => (
                <Select.Item value={version}>{version}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          <Button
            size="1"
            variant="outline"
            color="gray"
            onClick={() => {
              setVersionAFilter("");
              setVersionBFilter("");
              setVersionA("");
              setVersionB("");
            }}
          >
            Clear
          </Button>
          <Button
            size="1"
            variant="soft"
            color="gray"
            highContrast
            onClick={() => {
              if (versionA) {
                setVersionAFilter(versionA);
              }
              if (versionB) {
                setVersionBFilter(versionB);
              }
            }}
          >
            Apply
          </Button>
          <Dialog.Close>
            <IconButton size="1" variant="soft" color="gray">
              <Cross2Icon />
            </IconButton>
          </Dialog.Close>
        </Flex>
        <Box>
          <Heading size="3" align="center" mb="5">
            Zone-based Predictions Chart By Pipeline Versions
          </Heading>
          <Box className="relative z-20">
            <PredictionLineBarChart
              data={zoneBasedComparisonDataFiltered}
              isFetching={baseStats.isFetching || compareStats.isFetching}
              isBarChart={isBarChart}
              opacityKey="chart_line_opacity_zone_comparison"
              debounce={0}
              colorA={colorA}
              colorB={colorB}
              dataKeyA="zone_based_f1_score_A"
              dataKeyB="zone_based_f1_score_B"
              labelToValueMap={zoneBasedLabelMap}
              tooltipContent={<ComparisonTooltip />}
              isChartClickable={isZoneBasedChartClickable}
            />
          </Box>

          <Heading size="3" align="center" mb="5" mt="6">
            Pin-based Predictions Chart By Pipeline Versions
          </Heading>
          <PredictionLineBarChart
            data={pinBasedComparisonDataFiltered}
            isFetching={baseStats.isFetching || compareStats.isFetching}
            isBarChart={isBarChart}
            opacityKey="chart_line_opacity_pin_comparison"
            debounce={0}
            colorA={colorA}
            colorB={colorB}
            dataKeyA="pin_based_f1_score_A"
            dataKeyB="pin_based_f1_score_B"
            labelToValueMap={pinBasedLabelMap}
            tooltipContent={<ComparisonTooltip />}
            isChartClickable={isPinBasedChartClickable}
          />
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
}
